/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Page, Content, InfoCard } from '@backstage/core-components';
import {
  HomePageCompanyLogo,
} from '@backstage/plugin-home';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import Grid from '@material-ui/core/Grid';

import { catalogs, TOOL_ICONS, useHomeTools, useLogoStyles } from './shared';
import { LogoFullAccionaHome } from '../Root/LogoFullAcciona';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { useEffect, useState } from 'react';
import { Box, Button, Card, CardActions, CardContent, CardHeader, List, ListItemIcon, ListItemText, makeStyles, Typography } from '@material-ui/core';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { scaffolderReactTranslationRef } from '@backstage/plugin-scaffolder-react/alpha';
import { entityPageTranslationRef } from '../translation/entity-page/entity-page';
import { Link } from 'react-router-dom';
import { AnnouncementsPopup } from '@internal/plugin-announcements';

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    transition: '0.2s',
    display: 'flex',
    flexDirection: 'column',

    '&:hover': {
      boxShadow: theme.shadows[4],
      transform: 'translateY(-2px)',
    },
  },

  header: {
    color: theme.getPageTheme({ themeId: 'Proyecto' }).fontColor,
    padding: theme.spacing(2, 2, 3),
    backgroundImage: theme.getPageTheme({ themeId: 'Proyecto' }).backgroundImage,
    backgroundPosition: 0,
    backgroundSize: 'inherit',
  },

  content: {
    flexGrow: 1,
  },

  actions: {
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },

  description: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minHeight: 50,
  },
  toolkit: {
    display: 'flex',
    flexWrap: 'wrap',
    textAlign: 'center',
  },
  tool: {
    margin: theme.spacing(0.5, 1),
  },
  label: {
    marginTop: theme.spacing(1),
    width: '72px',
    fontSize: '0.9em',
    lineHeight: '1.25',
    overflowWrap: 'break-word',
    color: theme.palette.text.secondary,
  },
  icon: {
    width: '64px',
    height: '64px',
    borderRadius: '50px',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: theme.shadows[1],
    backgroundColor: theme.palette.background.default,
  },
}));

export const HomePage = () => {
  const classes = useStyles();
  const { container } = useLogoStyles();
  const catalogApi = useApi(catalogApiRef);
  const [templates, setTemplates] = useState([]);
  const { t } = useTranslationRef(scaffolderReactTranslationRef);
  const entityPageTranslation = useTranslationRef(entityPageTranslationRef);
  const tools = useHomeTools();

  useEffect(() => {
    // Any side-effects or data fetching can be done here
    catalogApi.getEntities({ filter: { kind: 'Template' }, order: { field: 'spec.type', order: 'asc' } }).then((templates: any) => {
      setTemplates(templates.items);
    })
  }, [catalogApi]);

  return (
    <SearchContextProvider>
      <Page themeId="home">
        {/* <Header title={<WelcomeTitle />} pageTitleOverride="Home">
          <HeaderWorldClock
            clockConfigs={clockConfigs}
            customTimeFormat={timeFormat}
          />
        </Header> */}
        <Content>
          <AnnouncementsPopup />
          <Grid container justifyContent="center" spacing={2}>
            <HomePageCompanyLogo
              className={container}
              logo={<LogoFullAccionaHome />}
            />
            <Grid item xs={12}>
              <InfoCard title={entityPageTranslation.t('homePage.selfServiceTitle')}>
                {(templates && templates?.length > 0) ? (
                  <Grid container spacing={3} justifyContent='space-evenly'>
                    {templates?.map((template: any) => (
                      <Grid item xs={12} sm={4} md={4} xl={4} key={template.metadata.uid}>
                        <Card className={classes.card}>
                          <CardHeader
                            className={classes.header}
                            title={
                              <Box>
                                {template.spec?.type && (
                                  <Typography variant="subtitle2" component="h3">
                                    {template.spec?.type}
                                  </Typography>
                                )}
                                {template.metadata.title && (
                                  <Typography variant="h6" component="h4">
                                    {template.metadata.title}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                          <CardContent>

                            <Typography variant="body1" gutterBottom>
                              {template.metadata?.description ?? '—'}
                            </Typography>

                          </CardContent>

                          {!template.spec?.comingSoon ? <CardActions className={classes.actions}>
                            <Button
                              size="small"
                              color="primary"
                              component="a"
                              href={`/create/templates/${template.metadata.namespace}/${template.metadata.name}`}
                            >
                              {t('templateCard.chooseButtonText')}
                            </Button>
                          </CardActions> : <CardActions className={classes.actions}>
                            <Typography variant="body1" gutterBottom>
                              {entityPageTranslation.t('homePage.comingSoon')}
                            </Typography>
                          </CardActions>}
                        </Card>
                      </Grid>
                    ))}
                  </Grid>) :
                  <Typography>{entityPageTranslation.t('homePage.selfServiceError')}</Typography>}
              </InfoCard>
            </Grid>
            <Grid container xs={12} spacing={2} style={{ marginTop: 5 }}>
              <Grid item xs={12}>
                <InfoCard title={entityPageTranslation.t('homePage.catalogTitle')}>
                  {(catalogs && catalogs?.length > 0) ? (
                    <Grid container spacing={3} justifyContent='space-evenly'>
                      {catalogs?.map((catalog: any) => (
                        <Grid item xs={12} sm={12} md={4} xl={3} key={catalog.id}>
                          <Card className={classes.card}>
                            <CardHeader
                              className={classes.header}
                              title={
                                <Box>
                                  {catalog.type && (
                                    <Typography variant="subtitle2" component="h3">
                                      {catalog.type}
                                    </Typography>
                                  )}
                                  {catalog.title && (
                                    <Typography variant="h6" component="h4">
                                      {catalog.title}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                            <CardContent>

                              <Typography variant="body1" gutterBottom>
                                {catalog.description ?? '—'}
                              </Typography>

                            </CardContent>

                            {catalog.url ? <CardActions className={classes.actions}>
                              <Button
                                size="small"
                                color="primary"
                                component="a"
                                href={`${catalog.url}`}
                              >
                                {t('templateCard.chooseButtonText')}
                              </Button>
                            </CardActions> : <CardActions className={classes.actions}>
                              <Typography variant="body1" gutterBottom>
                                {entityPageTranslation.t('homePage.comingSoon')}
                              </Typography>
                            </CardActions>}
                          </Card>
                        </Grid>
                      ))}
                    </Grid>) :
                    <Typography>{entityPageTranslation.t('homePage.catalogError')}</Typography>}
                </InfoCard>
              </Grid>
            </Grid>
            <Grid container item xs={12} spacing={0}>
              <Grid item xs={12}>
                <InfoCard title={entityPageTranslation.t('homePage.toolkitTitle')}>
                  <Grid container spacing={3} justifyContent='flex-start'>
                    <List className={classes.toolkit}>
                      {tools.map((tool: any) => (
                        <Link key={tool.url} to={tool.url} className={classes.tool}>
                          <ListItemIcon className={classes.icon}>{TOOL_ICONS[tool.key]}</ListItemIcon>
                          <ListItemText
                            secondaryTypographyProps={{ className: classes.label }}
                            secondary={tool.label}
                          />
                        </Link>
                      ))}
                    </List>
                  </Grid>
                </InfoCard>
              </Grid>
            </Grid>
          </Grid>
        </Content>
      </Page >
    </SearchContextProvider >
  );
};
